import { 
  Bell, 
  UserPlus, 
  CheckCircle, 
  Trophy, 
  Calendar, 
  Mail, 
  Settings, 
  AlertCircle,
  UserCheck,
  UserMinus,
  MessageSquare,
  ClipboardCheck 
} from 'lucide-react'
import type { NotificationType } from '@/services/notificationService'

// Map notification types to icons
export const notificationIcons: Record<NotificationType, typeof Bell> = {
  challenge: UserPlus,
  result: CheckCircle,
  ranking: Trophy,
  reminder: Calendar,
  invite: Mail,
  system: Settings,
  score_submission: ClipboardCheck,
  score_confirmed: CheckCircle,
  score_disputed: AlertCircle,
  member_joined: UserCheck,
  member_left: UserMinus,
  admin_message: MessageSquare,
}

// Map notification types to colors (for the icon background)
export const notificationColors: Record<NotificationType, { bg: string; text: string; unreadBg: string; unreadText: string }> = {
  challenge: { bg: 'bg-blue-100', text: 'text-blue-600', unreadBg: 'bg-blue-500/20', unreadText: 'text-blue-600' },
  result: { bg: 'bg-green-100', text: 'text-green-600', unreadBg: 'bg-green-500/20', unreadText: 'text-green-600' },
  ranking: { bg: 'bg-amber-100', text: 'text-amber-600', unreadBg: 'bg-amber-500/20', unreadText: 'text-amber-600' },
  reminder: { bg: 'bg-purple-100', text: 'text-purple-600', unreadBg: 'bg-purple-500/20', unreadText: 'text-purple-600' },
  invite: { bg: 'bg-indigo-100', text: 'text-indigo-600', unreadBg: 'bg-indigo-500/20', unreadText: 'text-indigo-600' },
  system: { bg: 'bg-gray-100', text: 'text-gray-600', unreadBg: 'bg-gray-500/20', unreadText: 'text-gray-600' },
  score_submission: { bg: 'bg-orange-100', text: 'text-orange-600', unreadBg: 'bg-orange-500/20', unreadText: 'text-orange-600' },
  score_confirmed: { bg: 'bg-green-100', text: 'text-green-600', unreadBg: 'bg-green-500/20', unreadText: 'text-green-600' },
  score_disputed: { bg: 'bg-red-100', text: 'text-red-600', unreadBg: 'bg-red-500/20', unreadText: 'text-red-600' },
  member_joined: { bg: 'bg-teal-100', text: 'text-teal-600', unreadBg: 'bg-teal-500/20', unreadText: 'text-teal-600' },
  member_left: { bg: 'bg-slate-100', text: 'text-slate-600', unreadBg: 'bg-slate-500/20', unreadText: 'text-slate-600' },
  admin_message: { bg: 'bg-pink-100', text: 'text-pink-600', unreadBg: 'bg-pink-500/20', unreadText: 'text-pink-600' },
}

// Default colors for unknown types
export const defaultNotificationColors = {
  bg: 'bg-muted',
  text: 'text-muted-foreground',
  unreadBg: 'bg-primary/10',
  unreadText: 'text-primary',
}

// Get colors for a notification type
export function getNotificationColors(type: NotificationType, read: boolean) {
  const colors = notificationColors[type] || defaultNotificationColors
  return {
    bg: read ? colors.bg : colors.unreadBg,
    text: read ? colors.text : colors.unreadText,
  }
}

// Format relative time for notifications
export function formatNotificationTime(dateStr: string): string {
  const date = new Date(dateStr);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / (1000 * 60));
  const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

  if (diffMins < 1) return 'Just now';
  if (diffMins < 60) return `${diffMins} minute${diffMins !== 1 ? 's' : ''} ago`;
  if (diffHours < 24) return `${diffHours} hour${diffHours !== 1 ? 's' : ''} ago`;
  if (diffDays < 7) return `${diffDays} day${diffDays !== 1 ? 's' : ''} ago`;
  
  return date.toLocaleDateString('en-US', { 
    month: 'short', 
    day: 'numeric',
    year: date.getFullYear() !== now.getFullYear() ? 'numeric' : undefined
  });
}

// Check if a notification has actions
export function hasActions(notification: { actions?: unknown[] }): boolean {
  return Array.isArray(notification.actions) && notification.actions.length > 0
}

// Check if a notification is expired
export function isExpired(notification: { expiresAt?: string }): boolean {
  if (!notification.expiresAt) return false
  return new Date(notification.expiresAt) < new Date()
}

// Check if notification is group-scoped
export function isGroupNotification(notification: { scope?: string }): boolean {
  return notification.scope === 'group'
}

// Get scope label for display
export function getScopeLabel(scope: string): string {
  return scope === 'group' ? 'Group' : 'Personal'
}

